import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Alert, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  CheckCircle, 
  Error, 
  Warning,
  Info,
  Refresh
} from '@mui/icons-material';
import { config } from '../config';

interface DiagnosticResult {
  test: string;
  status: 'success' | 'error' | 'warning' | 'info';
  message: string;
  details?: string;
}

export const B2CDiagnostic: React.FC = () => {
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const runDiagnostics = async () => {
    setIsRunning(true);
    setResults([]);
    
    const diagnosticResults: DiagnosticResult[] = [];

    // Test 1: Environment Variables
    diagnosticResults.push({
      test: 'Environment Variables',
      status: process.env.REACT_APP_B2C_CLIENT_ID ? 'success' : 'error',
      message: process.env.REACT_APP_B2C_CLIENT_ID ? 
        'B2C environment variables are configured' : 
        'Missing B2C environment variables',
      details: `Client ID: ${process.env.REACT_APP_B2C_CLIENT_ID || 'Not set'}`
    });

    // Test 2: Configuration Validation
    const hasValidConfig = 
      config.msalConfig.auth.clientId &&
      config.msalConfig.auth.authority &&
      config.msalConfig.auth.redirectUri;
    
    diagnosticResults.push({
      test: 'MSAL Configuration',
      status: hasValidConfig ? 'success' : 'error',
      message: hasValidConfig ? 
        'MSAL configuration is valid' : 
        'MSAL configuration is incomplete',
      details: `Authority: ${config.msalConfig.auth.authority}`
    });

    // Test 3: B2C Endpoint Connectivity
    try {
      const wellKnownUrl = `${config.msalConfig.auth.authority}/.well-known/openid-configuration`;
      const response = await fetch(wellKnownUrl);
      
      if (response.ok) {
        const data = await response.json();
        diagnosticResults.push({
          test: 'B2C Endpoint Connectivity',
          status: 'success',
          message: 'B2C endpoint is accessible',
          details: `Issuer: ${data.issuer}`
        });
      } else {
        diagnosticResults.push({
          test: 'B2C Endpoint Connectivity',
          status: 'error',
          message: `B2C endpoint returned ${response.status}`,
          details: `URL: ${wellKnownUrl}`
        });
      }
    } catch (error) {
      diagnosticResults.push({
        test: 'B2C Endpoint Connectivity',
        status: 'error',
        message: 'Failed to connect to B2C endpoint',
        details: `Error: ${error}`
      });
    }

    // Test 4: Redirect URI Validation
    const currentOrigin = window.location.origin;
    const configuredRedirectUri = config.msalConfig.auth.redirectUri;
    
    diagnosticResults.push({
      test: 'Redirect URI Configuration',
      status: configuredRedirectUri === currentOrigin ? 'success' : 'warning',
      message: configuredRedirectUri === currentOrigin ? 
        'Redirect URI matches current origin' : 
        'Redirect URI mismatch detected',
      details: `Current: ${currentOrigin}, Configured: ${configuredRedirectUri}`
    });

    // Test 5: Local Storage Access
    try {
      const testKey = 'msal-test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      diagnosticResults.push({
        test: 'Local Storage Access',
        status: 'success',
        message: 'Local storage is accessible',
        details: 'Required for token caching'
      });
    } catch (error) {
      diagnosticResults.push({
        test: 'Local Storage Access',
        status: 'error',
        message: 'Local storage is not accessible',
        details: `Error: ${error}`
      });
    }

    setResults(diagnosticResults);
    setIsRunning(false);
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const getStatusIcon = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'info':
        return <Info color="info" />;
      default:
        return <Info />;
    }
  };

  const getStatusColor = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const warningCount = results.filter(r => r.status === 'warning').length;

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Azure B2C Diagnostic
        </Typography>
        <Button
          variant="outlined"
          startIcon={isRunning ? <CircularProgress size={16} /> : <Refresh />}
          onClick={runDiagnostics}
          disabled={isRunning}
        >
          {isRunning ? 'Running...' : 'Run Diagnostics'}
        </Button>
      </Box>

      {results.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={`${successCount} Passed`} 
              color="success" 
              variant="outlined" 
            />
            {errorCount > 0 && (
              <Chip 
                label={`${errorCount} Failed`} 
                color="error" 
                variant="outlined" 
              />
            )}
            {warningCount > 0 && (
              <Chip 
                label={`${warningCount} Warnings`} 
                color="warning" 
                variant="outlined" 
              />
            )}
          </Box>
        </Box>
      )}

      {errorCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {errorCount} diagnostic test(s) failed. Please review the results below and fix the issues before attempting to log in.
          </Typography>
        </Alert>
      )}

      <List>
        {results.map((result, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemIcon>
                {getStatusIcon(result.status)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" component="span">
                      {result.test}
                    </Typography>
                    <Chip 
                      label={result.status.toUpperCase()} 
                      color={getStatusColor(result.status) as any}
                      size="small"
                    />
                  </Box>
                }
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {result.message}
                    </Typography>
                    {result.details && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {result.details}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
            {index < results.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Current Configuration:
        </Typography>
        <Typography variant="body2" component="pre" sx={{ 
          fontSize: '0.75rem',
          overflow: 'auto',
          wordBreak: 'break-all'
        }}>
          {JSON.stringify({
            clientId: config.msalConfig.auth.clientId,
            authority: config.msalConfig.auth.authority,
            redirectUri: config.msalConfig.auth.redirectUri,
            scopes: config.loginRequest.scopes
          }, null, 2)}
        </Typography>
      </Box>
    </Paper>
  );
};
